// Mounted once for the whole app. Raises a notice when a contract extraction
// the agent left running in the background finishes, wherever they are, and
// warns before the tab closes while one is still running (the job lives in
// memory; see src/services/contractExtractionJobs.js).

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useOptionalToast } from '../../../contexts/ToastContext';
import {
    subscribeContractExtractions, extractionNotice, requestContractExtractionReview,
    hasRunningContractExtraction,
} from '../../../services/contractExtractionJobs';

export default function ContractExtractionNotifier({ workspaceId }) {
    // showToast is stable; the context value itself changes with every toast.
    const { showToast } = useOptionalToast();
    const navigate = useNavigate();
    const workspaceIdRef = React.useRef(workspaceId);
    workspaceIdRef.current = workspaceId;

    React.useEffect(() => subscribeContractExtractions((job, previous) => {
        const notice = extractionNotice(previous, job);
        if (!notice) return;
        const type = notice.kind === 'ready' ? 'success' : 'error';
        // The deal route only resolves inside its own workspace; from another
        // one the notice says where to look instead of offering a dead link.
        if (notice.workspaceId !== workspaceIdRef.current) {
            showToast(`${notice.message} Open the deal in its workspace to see it.`, type, { duration: null });
            return;
        }
        showToast(notice.message, type, {
            duration: null,
            action: {
                label: notice.kind === 'ready' ? 'Review' : 'Open deal',
                onClick: () => {
                    requestContractExtractionReview(notice.transactionId);
                    navigate(`/transactions/${notice.transactionId}?tab=details`);
                },
            },
        });
    }), [showToast, navigate]);

    React.useEffect(() => {
        const onBeforeUnload = (event) => {
            if (!hasRunningContractExtraction()) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    return null;
}
